import { Close, MoreVert, PrintOutlined, ReceiptLongOutlined } from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ESTADO_IMPRESION, RANURA } from "../impresion";
import { describirEstadoImpresion } from "../impresion/etiquetas";
import { formatCurrency } from "../utils";

const COLOR_POR_TONO = {
  neutro: "#64748B",
  ok: "#18735F",
  error: "#B3261E",
  alerta: "#9A6209",
};

// Reimpresiones manuales. La comanda a cafetería no se ofrece: sale sola al
// cerrar la venta.
const OPCIONES_REIMPRESION = [
  { ranura: RANURA.FACTURA, etiqueta: "Reimprimir factura o constancia", corta: "Factura", Icono: ReceiptLongOutlined },
  { ranura: RANURA.COMANDA, etiqueta: "Reimprimir comanda", corta: "Comanda", Icono: PrintOutlined },
];

export default function RecentSalesDialog({ open, sales, onClose, onReimprimir, esDemo = false }) {
  // Estado de la última reimpresión pedida por venta y ranura. Es feedback de
  // esta sesión de pantalla, no un registro: la venta no guarda si se imprimió.
  const [estados, setEstados] = useState({});
  const [menu, setMenu] = useState({ anchor: null, sale: null });

  useEffect(() => {
    if (open) {
      setEstados({});
      setMenu({ anchor: null, sale: null });
    }
  }, [open]);

  const cerrarMenu = () => setMenu({ anchor: null, sale: null });

  const clave = (sale, ranura) => `${sale.idPedido}:${ranura}`;

  const handleReimprimir = async (sale, ranura) => {
    if (!onReimprimir || !sale.idPedido) return;
    const id = clave(sale, ranura);
    setEstados((previos) => ({ ...previos, [id]: { estado: ESTADO_IMPRESION.PREPARANDO, error: null } }));
    const resultado = await onReimprimir(sale, ranura);
    setEstados((previos) => ({ ...previos, [id]: resultado }));
  };

  const describir = (sale, ranura) => {
    const actual = estados[clave(sale, ranura)];
    if (!actual) return null;
    const descripcion = describirEstadoImpresion(actual.estado);
    return { texto: actual.error || descripcion.titulo, color: COLOR_POR_TONO[descripcion.tono] };
  };

  const enCurso = (sale) =>
    OPCIONES_REIMPRESION.some(({ ranura }) => estados[clave(sale, ranura)]?.estado === ESTADO_IMPRESION.PREPARANDO);

  const handleOpcion = (ranura) => {
    const { sale } = menu;
    cerrarMenu();
    if (sale) handleReimprimir(sale, ranura);
  };

  const puedeReimprimir = Boolean(onReimprimir) && !esDemo;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: 25, fontWeight: 900, color: "#172033" }}>Ventas recientes</Typography>
            <Typography sx={{ color: "#64748B", fontSize: 13 }}>
              {esDemo ? "Operaciones dummy del puesto actual" : "Operaciones del evento vigente"}
            </Typography>
          </Box>
          <IconButton aria-label="Cerrar ventas recientes" onClick={onClose}><Close /></IconButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {sales.map((sale) => {
            // Resultado de la última reimpresión pedida en esta sesión.
            const estadosReimpresion = OPCIONES_REIMPRESION.map(({ ranura, corta }) => ({
              etiqueta: corta,
              estado: describir(sale, ranura),
            })).filter(({ estado }) => estado);
            const ocupado = enCurso(sale);
            return (
              <Box
                key={sale.id}
                data-testid={`recent-sale-${sale.id}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "auto 1fr auto auto", md: "auto minmax(0,1.4fr) .6fr 1fr auto auto" },
                  gap: 1.5,
                  alignItems: "center",
                  p: 1.5,
                  border: "1px solid #E2E8F0",
                  borderRadius: "14px",
                }}
              >
                <Box sx={{ width: 42, height: 42, borderRadius: "12px", bgcolor: "#F3F2FF", display: "grid", placeItems: "center" }}>
                  <ReceiptLongOutlined sx={{ color: "#2C3087" }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 13, color: "#172033" }}>{sale.id} · {sale.time}</Typography>
                  <Typography noWrap sx={{ color: "#64748B", fontSize: 12 }}>{sale.customer}</Typography>
                </Box>
                <Typography sx={{ display: { xs: "none", md: "block" }, color: "#475569", fontSize: 12 }}>{sale.items} u.</Typography>
                <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                  <Typography sx={{ color: "#475569", fontSize: 12 }}>{sale.payment}</Typography>
                  {sale.benefit && (
                    <Typography noWrap sx={{ color: "#9A6209", fontSize: 10.5 }}>{sale.benefit}</Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontWeight: 900, color: "#172033", fontSize: 13 }}>{formatCurrency(sale.total)}</Typography>
                  <Chip size="small" label={sale.status} sx={{ height: 20, bgcolor: "#E8F7F2", color: "#18735F", fontSize: 10, fontWeight: 900 }} />
                </Box>
                {puedeReimprimir && sale.idPedido && (
                  <IconButton
                    size="small"
                    aria-label={`Opciones de reimpresión de ${sale.id}`}
                    disabled={ocupado}
                    onClick={(event) => setMenu({ anchor: event.currentTarget, sale })}
                    data-testid={`reimprimir-menu-${sale.id}`}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                )}
                {estadosReimpresion.length > 0 && (
                  <Box sx={{ gridColumn: "1 / -1", display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    {estadosReimpresion.map(({ etiqueta, estado }) => (
                      <Typography key={etiqueta} sx={{ fontSize: 11, fontWeight: 700, color: estado.color }}>
                        {etiqueta}: {estado.texto}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        <Menu
          anchorEl={menu.anchor}
          open={Boolean(menu.anchor)}
          onClose={cerrarMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {OPCIONES_REIMPRESION.map(({ ranura, etiqueta, Icono }) => (
            <MenuItem
              key={ranura}
              onClick={() => handleOpcion(ranura)}
              data-testid={`reimprimir-${ranura}-${menu.sale?.id}`}
            >
              <ListItemIcon>
                <Icono fontSize="small" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 13 }}>{etiqueta}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </DialogContent>
    </Dialog>
  );
}

RecentSalesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  sales: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  // (venta, ranura) => Promise<{ estado, error }>
  onReimprimir: PropTypes.func,
  esDemo: PropTypes.bool,
};
